package nl.volleyplanner.ktor

import io.ktor.client.engine.*
import io.ktor.client.plugins.logging.*
import io.ktor.client.request.*
import io.ktor.http.*
import io.ktor.util.date.*
import io.ktor.utils.io.*
import io.ktor.utils.io.core.*
import kotlinx.coroutines.CompletableJob
import kotlinx.coroutines.Job
import kotlinx.io.readByteArray
import nl.volleyplanner.nevoboservice.api.ktorLogger
import nl.volleyplanner.nevoboservice.extensions.replaceAccents
import okio.ByteString.Companion.encodeUtf8
import okio.FileSystem
import okio.Path
import okio.Path.Companion.toPath
import okio.buffer
import kotlin.coroutines.CoroutineContext
import kotlin.random.Random

class MockHttpClientEngineConfig : HttpClientEngineConfig()

enum class UseMockStrategy(val readMock: Boolean, val useDelegate: Boolean) {
    MockAndDelegate(true, true),
    MockOnly(true, false),
    DelegateOnly(false, true)
}

data class MockConfig(
    val createMockResponses: Boolean,
    val useMock: UseMockStrategy,
    val mockDirectory: Path
) {
    companion object {
        val NO_MOCK = MockConfig(false, UseMockStrategy.DelegateOnly, "./.mock-response-data/".toPath(true))
    }

    val mockEnabled = createMockResponses || useMock.readMock
}

class MockFileHandler(
    val mockFileSystem: FileSystem,
    val mockConfig: MockConfig
) {
    init {
        if (mockConfig.mockEnabled) {
            mockFileSystem.createDirectories(mockConfig.mockDirectory, mustCreate = false)
        }
    }

    fun resolveMockFilePath(url: Url) : Path? {
        if (!mockConfig.mockEnabled) return null

        val urlPath = url.segments.dropLast(1).joinToString("/", postfix="/")
        val requestMockDir = mockConfig.mockDirectory.resolve(urlPath)
        mockFileSystem.createDirectories(requestMockDir, mustCreate = false)

        val queryStringSorted = url.parameters.entries()
            .sortedBy { it.key }
            .flatMap { (key, values) -> values.map { value -> "$key=$value" } }
            .joinToString("&")
        return buildString {
            append(url.segments.last())
            if (queryStringSorted.isNotBlank()) {
                append("___")
                append(queryStringSorted.toRequestFilePath())
            }
        }.let { filename ->
            if (filename.length > 200) {
                val hash = filename.encodeUtf8().sha256().hex().take(16)
                filename.take(200).trimEnd('-', '_') + "___$hash"
            } else {
                filename
            }
        }.let { requestMockDir.resolve("$it.json", true) }
    }

    fun String.toRequestFilePath() : String {
        return this
            .lowercase()
            .replaceAccents()
            .replace("[=/]".toRegex(), "-")
            .replace("&", "__")
            .replace(
                "[^=&_a-z0-9\\s-]".toRegex(),
                ""
            )         // Remove non-alphanumeric characters except spaces and hyphens
            .trim('-')
    }
}

class MockingHttpClientEngine(
    val delegate: HttpClientEngine,
    val mockFileSystem: FileSystem,
    val mockConfig: MockConfig,
    val logger: Logger
) : HttpClientEngineBase("ktor-mock-volleyplanner") {
    override val config: HttpClientEngineConfig = MockHttpClientEngineConfig()

    val mockFileHandler = MockFileHandler(mockFileSystem, mockConfig)

    private fun completedCallContext(parent: CoroutineContext): CoroutineContext {
        val callJob: CompletableJob = Job(parent[Job])
        callJob.complete()
        return parent.minusKey(Job) + callJob
    }

    @InternalAPI
    override suspend fun execute(data: HttpRequestData): HttpResponseData {
        val requestData = data
        // TODO: Take request method into account. Also, always assuming JSON as response.
        val requestMockFilePath = if (mockConfig.mockEnabled) {
            mockFileHandler.resolveMockFilePath(requestData.url)
                .also {
                    logger.log("Mock file path: $it")
                }
        } else null

        val httpResponseData = readMockResponse(requestMockFilePath, completedCallContext(data.executionContext))
            ?: mockConfig.useMock.takeIf { it.useDelegate }?.let {
                val response = delegate.execute(requestData)
                if (mockConfig.createMockResponses) {
                    ktorLogger.log("Creating mock file: $requestMockFilePath")

                    HttpResponseData(
                        statusCode = response.statusCode,
                        requestTime = response.requestTime,
                        headers = response.headers,
                        version = response.version,
                        body = (response.body as ByteReadChannel).snapshotToMockFile(
                            mockPath = requestMockFilePath!!
                        ),
                        callContext = completedCallContext(requestData.executionContext)
                    )
                } else {
                    response
                }
            } ?: return HttpResponseData(HttpStatusCode.NotFound, GMTDate(), headersOf(), HttpProtocolVersion.HTTP_1_1, ByteReadChannel.Empty, data.executionContext)

        return httpResponseData
    }

    fun readMockResponse(requestMockFilePath: Path?, callContext: CoroutineContext) : HttpResponseData? {
        if (requestMockFilePath == null) return null
        if (!mockConfig.mockEnabled) return null
        if (mockConfig.useMock.readMock) {
            if (!mockFileSystem.exists(requestMockFilePath)) {
                ktorLogger.log("Mock file does not exist: $requestMockFilePath")
                return null
            }

            ktorLogger.log("Reading mock file: $requestMockFilePath")
            val bytes = mockFileSystem.read(requestMockFilePath) { readUtf8() }.toByteArray()
            return HttpResponseData(
                statusCode = HttpStatusCode.OK,
                requestTime = GMTDate(),
                // TODO: Somehow store response headers with the response. So we can set the right Content-Type
                headers = headers {
                    append(HttpHeaders.ContentLength, bytes.size.toString())
                    append(HttpHeaders.ContentType, "application/ld+json; charset=utf+8")
                },
                version = HttpProtocolVersion.HTTP_1_1,
                body = ByteReadChannel(bytes),
                callContext = callContext
            )
        }
        return null
    }

    private suspend fun ByteReadChannel.snapshotToMockFile(mockPath: Path): ByteArray {
        val bytes = readRemaining().readByteArray()

        val temporaryPath = mockPath.parent!!.resolve(
            "${mockPath.name}.${Random.nextLong().toString(16)}.tmp",
            normalize = true
        )

        try {
            val sink = mockFileSystem.sink(temporaryPath, mustCreate = false).buffer()
            try {
                sink.write(bytes)
            } finally {
                sink.close()
            }

            mockFileSystem.atomicMove(temporaryPath, mockPath)
            ktorLogger.log("Created mock file: $mockPath")
        } catch (throwable: Throwable) {
            runCatching {
                if (mockFileSystem.exists(temporaryPath)) {
                    mockFileSystem.delete(temporaryPath)
                }
            }

            throw throwable
        }

        return bytes
    }

    override fun close() {
        delegate.close()
        mockFileSystem.close()
        super.close()
    }
}